import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import type { MusicVideoClip } from 'fym-shared/src/music24/musicVideoDoc';
import { newDocId } from '../doc/clipIds';
import { moveClip } from '../doc/clipOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import type { DragGuide } from './TimelineGuide';
import { snapTime, type TimelineView } from './timelineGeometry';

interface UseClipMoveProps {
  laneRef: RefObject<HTMLDivElement | null>;
  view: TimelineView;
  snapOn: boolean;
  onGuide: (guide: DragGuide | null) => void;
}

/** The new place of a clip while it is dragged (the dashed box on the lane). */
export interface ClipMoveGhost {
  id: string;
  startSec: number;
  endSec: number;
}

/** A press moves the clip only after the pointer went this far; a shorter move is a click. */
const MOVE_THRESHOLD_PX = 4;

interface ClipDrag {
  id: string;
  pointerId: number;
  startX: number;
  startSec: number;
  lengthSec: number;
  moved: boolean;
}

/**
 * Drags a whole clip to another place (design c5: "drag to move"). The start snaps to the beat (Alt
 * or snap off: free). The old place fills like a remove; the clip covers what is at the new place
 * (`moveClip`). One drag is one Undo step. A press with no move stays a click (select the clip);
 * Escape cancels a drag.
 */
export function useClipMove({ laneRef, view, snapOn, onGuide }: UseClipMoveProps) {
  const { change, songMap, select, readOnly } = useMusicVideoEditor();
  const drag = useRef<ClipDrag | null>(null);
  const [ghost, setGhost] = useState<ClipMoveGhost | null>(null);
  // The click that the browser sends after a drag must not select the clip again or seek.
  const dragEnded = useRef(false);

  const placeOf = useCallback(
    (current: ClipDrag, clientX: number, altKey: boolean): number | null => {
      const lane = laneRef.current;
      if (lane === null) {
        return null;
      }
      const { width } = lane.getBoundingClientRect();
      if (width <= 0) {
        return null;
      }
      const deltaSec = ((clientX - current.startX) / width) * (view.to - view.from);
      return snapTime(current.startSec + deltaSec, songMap.beats, songMap.bpm, !snapOn || altKey);
    },
    [laneRef, snapOn, songMap.beats, songMap.bpm, view.from, view.to]
  );

  const cancel = useCallback(() => {
    drag.current = null;
    setGhost(null);
    onGuide(null);
  }, [onGuide]);

  const onPointerDown = useCallback(
    (clip: MusicVideoClip, event: PointerEvent<HTMLElement>) => {
      dragEnded.current = false;
      if (readOnly || event.button !== 0) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        id: clip.id,
        pointerId: event.pointerId,
        startX: event.clientX,
        startSec: clip.startSec,
        lengthSec: clip.endSec - clip.startSec,
        moved: false,
      };
    },
    [readOnly]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const { current } = drag;
      if (current === null || current.pointerId !== event.pointerId) {
        return;
      }
      if (!current.moved && Math.abs(event.clientX - current.startX) < MOVE_THRESHOLD_PX) {
        return;
      }
      current.moved = true;
      const sec = placeOf(current, event.clientX, event.altKey);
      if (sec === null) {
        return;
      }
      setGhost({ id: current.id, startSec: sec, endSec: sec + current.lengthSec });
      onGuide({ sec, free: !snapOn || event.altKey });
    },
    [onGuide, placeOf, snapOn]
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const { current } = drag;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (current === null || current.pointerId !== event.pointerId) {
        return;
      }
      cancel();
      if (!current.moved) {
        return;
      }
      dragEnded.current = true;
      const sec = placeOf(current, event.clientX, event.altKey);
      if (sec !== null) {
        change((doc) => moveClip(doc, current.id, sec, () => newDocId()));
        select({ kind: 'clip', id: current.id });
      }
    },
    [cancel, change, placeOf, select]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape' && drag.current !== null) {
        event.stopPropagation();
        cancel();
      }
    },
    [cancel]
  );

  /** True once for the click that ends a drag: the lane skips its click action. */
  const takeDragEnd = useCallback((): boolean => {
    const ended = dragEnded.current;
    dragEnded.current = false;
    return ended;
  }, []);

  return { ghost, onPointerDown, onPointerMove, onPointerUp, onPointerCancel: cancel, onKeyDown, takeDragEnd };
}
